import React from "react";
import Button from "./Button";
import { cn } from "./cn";

/**
 * Bun avatar: initials on a soft identity chip. Tones reuse the status
 * pairs so household members stay distinguishable but calm.
 * size: "xs" 22 | "sm" 28 | "md" 30 | "lg" 34 | "xl" 42
 * onClick composes a standard icon action; aria-label describes its purpose.
 */
const SIZES = {
  xs: "w-[22px] h-[22px] text-[8px]",
  sm: "w-7 h-7 text-[10px]",
  md: "w-[30px] h-[30px] text-[11px]",
  lg: "w-[34px] h-[34px] text-[12px]",
  xl: "w-[42px] h-[42px] text-[14px]",
};

const TONES = {
  neutral: "bg-bun-chip-bg text-bun-chip-text",
  blue: "bg-bun-ring text-bun-blue-hover",
  special: "bg-bun-special-bg text-bun-special",
  warning: "bg-bun-warning-bg text-bun-warning",
  success: "bg-bun-success-bg text-bun-success",
  danger: "bg-bun-danger-bg text-bun-danger",
  ink: "bg-bun-ink text-bun-on-ink",
};

/** Derive up-to-two initials from a full name ("Maya Patel" -> "MP"). */
export function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function Avatar({
  initials,
  name,
  src,
  alt,
  onError,
  size = "md",
  tone = "neutral",
  variant,
  unframed = false,
  overlap,
  onClick,
  disabled = false,
  "aria-label": ariaLabel,
  className,
}) {
  const [imageFailed, setImageFailed] = React.useState(false);

  React.useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const handleImageError = (event) => {
    setImageFailed(true);
    onError?.(event);
  };

  const avatar = (
    <span
      className={cn(
        "inline-flex items-center justify-center overflow-hidden rounded-full font-instrument font-semibold select-none shrink-0",
        SIZES[size],
        TONES[tone],
        variant === "logo" && (unframed
          ? "rounded-bun bg-transparent"
          : "rounded-bun border border-bun-line bg-bun-surface p-1"),
        overlap && "border-2 border-bun-surface",
        overlap && !onClick && "-ml-[10px] first:ml-0",
        className
      )}
    >
      {src && !imageFailed ? (
        <img
          src={src}
          alt={alt ?? name ?? ""}
          onError={handleImageError}
          className={cn("w-full h-full", variant === "logo" ? "object-contain" : "object-cover")}
        />
      ) : (
        initials || initialsOf(name)
      )}
    </span>
  );

  return onClick ? (
    <Button
      variant="ghost"
      iconOnly
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || name}
      className={cn(
        'shrink-0 focus-visible:z-10',
        size === 'xl' && 'h-[42px] w-[42px]',
        overlap && '-ml-[10px] first:ml-0'
      )}
    >
      {avatar}
    </Button>
  ) : avatar;
}

/** Overlapping avatar cluster, e.g. the two household members on a header. */
export function AvatarGroup({ children, className }) {
  return <span className={cn("inline-flex items-center", className)}>{children}</span>;
}
